import { createGlobalState } from '@vueuse/core';
import { getCurrentInstance } from 'vue';

function useContext() {
	const instance = getCurrentInstance();

	if (instance?.proxy == null) {
		throw new Error(
			'Current instance not found. Ensure useContext() is called inside a component setup() function.'
		);
	}

	return instance.proxy.$context;
}

/**
 * createSafeGlobalState
 * ---------------------
 * • Client  – identical to createGlobalState (one singleton per tab).
 * • Server  – one singleton per *request* (stored on nuxtApp),
 *             so no cross-request state pollution.
 */
export function createSafeGlobalState<T>(factory: () => T) {
	// One key per invocation of this helper
	const key = Symbol('safeGlobalState');

	// Client singleton – created once and kept forever
	const getClientState = createGlobalState(factory);

	return () => {
		if (process.client) return getClientState();

		// 🌐 server – scoped to the current Nuxt app (= current request)
		const context = useContext();

		// Lazily initialise per request
		if (!context._safeGlobalStates) context._safeGlobalStates = new Map<symbol, unknown>();

		if (!context._safeGlobalStates.has(key)) context._safeGlobalStates.set(key, factory());

		return context._safeGlobalStates.get(key) as T;
	};
}
